import { DIFFICULTIES } from '../config';
import { scoreWord } from './scoring';
import type { Difficulty, FallingWord, GameEvent, GameSnapshot, Projectile, SessionStats, VocabularyItem } from '../types';

const WIDTH = 1000;
const DEFENSE_Y = 610;

const freshStats = (startedAt: number): SessionStats => ({ score: 0, correctKeys: 0, wrongKeys: 0, wordsDestroyed: 0, wordsMissed: 0, combo: 0, bestCombo: 0, startedAt, activePlayMs: 0 });

export class GameEngine {
  private words: FallingWord[] = [];
  private projectiles: Projectile[] = [];
  private targetId: string | null = null;
  private stats: SessionStats;
  private hearts = 3;
  private state: GameSnapshot['state'] = 'countdown';
  private countdown = 3;
  private lastCountdown = 3;
  private spawnElapsed = 0;
  private countElapsed = 0;
  private instance = 0;
  private learning: VocabularyItem | null = null;
  private wrongKey: string | null = null;
  private events: GameEvent[] = [];
  private targetStartedAt = 0;
  private recentInitials: string[] = [];

  public constructor(private readonly vocab: VocabularyItem[], private readonly difficulty: Difficulty, private readonly now: () => number = () => performance.now(), private readonly categories: string[] | null = null) {
    this.stats = freshStats(this.now());
  }

  public update(dt: number): void {
    const safeDt = Math.min(Math.max(dt, 0), 0.05);
    this.wrongKey = null;
    if (this.state === 'paused' || this.state === 'over') return;
    if (this.state === 'countdown') {
      this.countElapsed += safeDt;
      this.countdown = Math.max(0, 3 - Math.floor(this.countElapsed));
      if (this.countdown !== this.lastCountdown && this.countdown > 0) {
        this.lastCountdown = this.countdown;
        this.events.push({ type: 'COUNTDOWN_TICK', value: this.countdown });
      }
      if (this.countElapsed >= 3) { this.state = 'playing'; this.countdown = 0; this.spawn(); }
      return;
    }
    this.stats.activePlayMs += safeDt * 1000;
    this.spawnElapsed += safeDt * 1000;
    const config = DIFFICULTIES[this.difficulty];
    if (this.spawnElapsed >= config.spawnMs && this.words.length < config.maxWords) {
      if (this.spawn()) this.spawnElapsed = 0;
    }
    for (const word of this.words) word.y += word.speed * safeDt * (word.locked && this.difficulty === 'beginner' ? 0.9 : 1);
    for (const projectile of this.projectiles) projectile.elapsed += safeDt;
    this.projectiles = this.projectiles.filter((shot) => shot.elapsed < shot.duration);
    const missed = this.words.filter((word) => word.y > DEFENSE_Y && !word.destroyed);
    for (const word of missed) this.miss(word);
    this.words = this.words.filter((word) => word.y <= DEFENSE_Y && !word.destroyed);
  }

  public handleKey(rawKey: string): void {
    if (this.state !== 'playing' || rawKey.length !== 1 || !/^[a-z]$/i.test(rawKey)) return;
    const key = rawKey.toLowerCase();
    let target = this.target();
    if (!target) {
      const candidates = this.words.filter((word) => word.word[0] === key).sort((a, b) => b.y - a.y || a.x - b.x);
      target = candidates[0];
      if (!target) return this.wrong(key);
      this.targetId = target.instanceId;
      target.locked = true;
      this.targetStartedAt = this.now();
      this.events.push({ type: 'TARGET_LOCKED', wordId: target.id });
    }
    const expected = target.word[target.typedIndex];
    if (expected !== key) return this.wrong(key);
    target.typedIndex += 1;
    this.stats.correctKeys += 1;
    const distance = Math.hypot(target.x - WIDTH / 2, target.y - 650);
    this.projectiles.push({ startX: WIDTH / 2, startY: 650, targetX: target.x, targetY: target.y, elapsed: 0, duration: Math.min(0.18, Math.max(0.08, distance / 4500)) });
    this.events.push({ type: 'KEY_CORRECT', wordId: target.id, index: target.typedIndex - 1 });
    if (target.typedIndex === target.word.length) this.destroy(target);
  }

  public togglePause(): void { if (this.state === 'playing') this.state = 'paused'; else if (this.state === 'paused') this.state = 'playing'; }
  public end(): void { if (this.state === 'playing' || this.state === 'countdown') this.state = 'over'; }
  public snapshot(): GameSnapshot { return { words: [...this.words], projectiles: [...this.projectiles], targetId: this.targetId, stats: { ...this.stats }, hearts: this.hearts, state: this.state, countdown: this.countdown, learning: this.learning, wrongKey: this.wrongKey }; }
  public drainEvents(): GameEvent[] { const events = [...this.events]; this.events = []; return events; }

  private spawn(): boolean {
    const { lengths: [min, max], vocabRange: [minDifficulty, maxDifficulty] } = DIFFICULTIES[this.difficulty];
    const categoryPool = this.categories ? this.vocab.filter((item) => this.categories?.includes(item.category)) : this.vocab;
    const suitable = categoryPool.filter((item) => item.word.length >= min && item.word.length <= max && item.difficulty >= minDifficulty && item.difficulty <= maxDifficulty && !this.words.some((word) => word.id === item.id));
    const pool = suitable.length > 0 ? suitable : categoryPool;
    const balancedPool = pool.filter((item) => !this.recentInitials.includes(item.word[0]));
    const item = (balancedPool.length > 0 ? balancedPool : pool)[Math.floor(Math.random() * (balancedPool.length > 0 ? balancedPool.length : pool.length))];
    this.recentInitials = [...this.recentInitials, item.word[0]].slice(-3);
    const x = this.findSafeSpawnX();
    if (x === null) return false;
    const speed = DIFFICULTIES[this.difficulty].speed * (0.85 + Math.random() * 0.3);
    // Larger arcade word cards need to enter fully inside the canvas rather than being clipped at the top edge.
    this.words.push({ ...item, instanceId: `${item.id}-${this.instance++}`, x, y: 70, speed, typedIndex: 0, locked: false, destroyed: false });
    this.events.push({ type: 'WORD_SPAWNED', wordId: item.id });
    return true;
  }

  private findSafeSpawnX(): number | null {
    const spawnY = 70;
    const cardHalfWidth = 132;
    const cardHeight = 124;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const candidate = 120 + Math.random() * (WIDTH - 240);
      const collision = this.words.some((word) => Math.abs(word.x - candidate) < cardHalfWidth * 2 && Math.abs(word.y - spawnY) < cardHeight);
      if (!collision) return candidate;
    }
    return null;
  }

  private target(): FallingWord | undefined { return this.words.find((word) => word.instanceId === this.targetId); }
  private wrong(key: string): void { this.stats.wrongKeys += 1; this.stats.combo = 0; this.wrongKey = key; this.events.push({ type: 'KEY_WRONG', key }, { type: 'COMBO_CHANGED', combo: 0 }); }
  private destroy(word: FallingWord): void {
    word.destroyed = true;
    this.stats.combo += 1;
    this.stats.bestCombo = Math.max(this.stats.bestCombo, this.stats.combo);
    const gained = scoreWord(word.word.length, this.stats.combo, this.difficulty, this.now() - this.targetStartedAt);
    this.stats.score += gained;
    this.stats.wordsDestroyed += 1;
    this.learning = word;
    this.targetId = null;
    this.events.push({ type: 'WORD_DESTROYED', wordId: word.id, score: gained }, { type: 'COMBO_CHANGED', combo: this.stats.combo });
  }
  private miss(word: FallingWord): void {
    word.destroyed = true;
    this.hearts -= 1;
    this.stats.wordsMissed += 1;
    this.stats.combo = 0;
    if (this.targetId === word.instanceId) this.targetId = null;
    this.events.push({ type: 'WORD_MISSED', wordId: word.id }, { type: 'COMBO_CHANGED', combo: 0 });
    if (this.hearts <= 0) { this.state = 'over'; this.events.push({ type: 'GAME_OVER' }); }
  }
}
